<template>
	<div
		class="content-header has-margin has-padding-top-3 has-bottom-divider"
	>
		<div class="field is-grouped is-flex-grow-1 is-align-items-end">
			<VLink
				v-if="canEditRestApiUsers"
				:text="$t('ra.apiUserList.addApiUser')"
				icon="plus"
				:hasUnderline="true"
				@click.prevent.stop="createNewClicked"
				class="has-margin-right-3 has-margin-bottom-05"
			/>

			<VSearch v-model="search" />
		</div>
	</div>

	<div class="scrollable-content">
		<SortableDataTable
			:rowClickedFn="rowClicked"
			:scrollable="false"
			:data="filteredData"
			defaultSort="userName"
			:columns="columns"
			:emptyText="$t('ra.apiUserList.table.noResults')"
		/>
	</div>
</template>

<script>
import { useStore } from 'vuex';
import { reactive, toRefs } from '@vue/reactivity';
import {
	getStoreAction,
	getStoreMutation,
} from '@assets/scripts/store/config';
import { formatForTable } from '@modules/RestApiBuilder/components/apiuser';
import Helpers from '@assets/scripts/helpers';
import { useApiAsync } from '@assets/scripts/composables/useApi';
import usePermission from '@assets/scripts/composables/usePermission';
import { GET_API_USERS } from '@modules/RestApiBuilder/endpoints';

export default {
	name: 'ApiUserList',
	data: function () {
		return {
			search: '',
		};
	},
	async setup() {
		const store = useStore();

		const state = reactive({
			apiUsers: [],
		});

		const loadApiUsers = async () => {
			// get all Api Users
			state.apiUsers = await useApiAsync(GET_API_USERS);
		};

		// subscribe to store action
		const unsubscribeAction = store.subscribeAction({
			after: async ({ type }) => {
				// check action type
				if (type === getStoreAction('UPDATE_API_USER_LIST', 'RA')) {
					// reload api user list if needed
					await loadApiUsers();
				}
			},
		});

		await loadApiUsers();

		return { ...toRefs(state), unsubscribeAction };
	},
	computed: {
		/**
		 * Boolean to indicate whether the current
		 * user can manage API users
		 */
		canEditRestApiUsers: function () {
			return usePermission('Upsert', 'RestApiBuilder');
		},
		columns: function () {
			return [
				{
					label: this.$t('ra.apiUserList.table.columns.userName'),
					field: 'userName',
					sortable: true,
					searchable: true,
					cellClass: 'has-text-weight-semibold',
				},
				{
					label: this.$t('ra.apiUserList.table.columns.isActive'),
					field: 'isActive',
					sortable: true,
					searchable: true,
				},
				{
					label: this.$t('ra.apiUserList.table.columns.last_time_edited'),
					field: 'last_time_edited',
					sortable: true,
					searchable: true,
					isDate: true,
				},
				{
					label: '',
					field: 'edit',
					sortable: false,
					default: '',
					component: 'VButton',
					cellClass: 'is-button-tool',
					args: {
						edit: {
							title: this.$t('general.edit'),
							isTool: true,
							icon: 'edit',
						},
						view: {
							title: this.$t('general.inspect'),
							isTool: true,
							icon: 'eye',
						}
					},
					click: this.rowClicked,
				},
				{
					label: '',
					field: 'delete',
					sortable: false,
					default: '',
					component: 'VButton',
					cellClass: 'is-button-tool',
					args: {
						href: '',
						title: this.$t('general.delete'),
						isTool: true,
						class: '',
						icon: 'delete',
					},
					click: this.deleteClicked,
				},
			];
		},
		tableFields: function () {
			return formatForTable(this.apiUsers);
		},
		searchableCols: function () {
			return Helpers.getSearchableColumns(this.columns);
		},
		filteredData: function () {
			// filter on search string
			return Helpers.filterByString(
				this.tableFields,
				this.search,
				this.searchableCols
			);
		},
	},
	methods: {
		rowClicked({ user }) {
			// open drawer with API user information
			this.$store.dispatch(
				getStoreAction('LOAD_AND_SHOW_API_USER', 'RA'),
				user
			);
		},
		deleteClicked({ id }) {
			// ask confirmation to delete document
			this.$store.commit(getStoreMutation('OPEN_CONFIRMATION'), {
				title: this.$t('ra.apiUserList.delete.confirm.title'),
				body: this.$t('ra.apiUserList.delete.confirm.body'),
				confirmButtonText: this.$t(
					'ra.apiUserList.delete.confirm.confirmButtonText'
				),
				confirmFn: () => {
					// dispatch action to delete clicked API user
					this.$store.dispatch(
						getStoreAction('DELETE_API_USER', 'RA'),
						id
					);
				},
			});
		},
		createNewClicked: function () {
			// open drawer with API user information
			this.$store.dispatch(getStoreAction('LOAD_AND_SHOW_API_USER', 'RA'));
		},
	},
	unmounted: function () {
		// unsubscribe from actions
		this.unsubscribeAction();
	},
};
</script>
